import type {ContextProvider, ContextProviderDataGeneric} from '@bitstillery/common/lib/context'
import m, {nextTick, state} from '@bitstillery/mithril'
import {copy_object, merge_deep, parse_query_params_for_filters, present, to_query_params} from '@bitstillery/common/lib/utils'
import {
    FilterSelectMultiple,
    FilterSelectSingle,
    FilterSelectRange,
    FilterToggle,
    FilterText,
    FilterWorkflow,
} from '@bitstillery/common/components'
import {$s, logger, store} from '@bitstillery/common/app'
import {unchecked} from '@bitstillery/common/lib/unchecked'

import type {FiltersDescription, Filters, FiltersFromDescription} from '@bitstillery/common/types'
import type {CollectionProxy} from '@bitstillery/common/lib/collection'
import type {CollectionSort} from '@bitstillery/common/lib/collection'
import type {CollectionLookup} from '@bitstillery/common/lib/state'
import type {Filter, FilterSelection, FilterSelectRangeState} from '@bitstillery/common/types'

/**
 * Writes a selection to a filter of any type. The value is one the filter's own `serialize` produced
 * (or its unset/default value), a pairing the Filter union can't express; written through this view,
 * the union's members don't each demand their own selection type.
 */
function set_selection(filter: {selection: FilterSelection}, selection: FilterSelection) {
    filter.selection = selection
}

/**
 * Replaces the selection of a filter whose `serialize` is a list, in place so it keeps its state proxy.
 * A filter that holds a single value has no list to replace and is left as it is.
 */
function replace_list_selection(filter: Filter, values: readonly unknown[]) {
    if (Array.isArray(filter.selection)) {
        const selection: unknown[] = filter.selection
        selection.splice(0, selection.length, ...values)
    }
}

/** The selection with nothing selected for a `serialize` type; a range filter's unset is its default instead. */
function unset_serialized_selection(data_type: Filter['serialize'] | undefined): FilterSelection {
    if (data_type === 'string') {
        return ''
    } else if (data_type === 'number') {
        // Not the null type is used here, because the watcher can't handle it.
        return ''
    } else if (data_type === 'boolean') {
        return false
    } else if (Array.isArray(data_type)) {
        return []
    }
    return undefined
}

/**
 * Build a [min, max] tuple from API RangeFilterValue or tuple; null max + infinity → Infinity.
 */
function normalizeRangeTuple(def: unknown, opts: {infinity?: boolean | undefined; scaleMax?: number | undefined}): number[] {
    const scaleMax = opts.scaleMax ?? 0
    if (Array.isArray(def) && def.length >= 2) {
        const lo = Number(def[0]) || 0
        const hi = def[1] === null || def[1] === undefined ? (opts.infinity ? Infinity : scaleMax) : Number(def[1])
        return [lo, hi]
    }
    if (def && typeof def === 'object' && 'min_value' in def) {
        const lo = Number(def.min_value) || 0
        const max_value = 'max_value' in def ? def.max_value : undefined
        const hi = max_value === null || max_value === undefined ? (opts.infinity ? Infinity : scaleMax) : Number(max_value)
        return [lo, hi]
    }
    return [0, opts.infinity ? Infinity : scaleMax]
}

/** The meta endpoint can answer `scale` as a `{min_value, max_value}` object instead of the declared tuple (#2423). */
function scale_max(scale: FilterSelectRangeState['scale'] | {min_value?: number; max_value?: number} | undefined): number {
    return Array.isArray(scale) ? scale[1] : (scale?.max_value ?? 0)
}

function normalizeSelectRangeDefault(filter: Filter): number[] {
    if (filter.type !== 'SELECT_RANGE') {
        return [0, 0]
    }
    return normalizeRangeTuple(filter.default, {infinity: filter.infinity, scaleMax: scale_max(filter.scale)})
}

/**
 * Returns how many filters are currently *activated* (not active).
 * With meta_default, the *default* value from the meta endpoint
 * is used to determine if a filter has been activated.
 */
export function activated_filters(collection: CollectionProxy): number {
    let active = 0

    const included_filters = Object.entries(collection.filters).filter(([filter_name]) => {
        return !collection.state.lookup.ignore_filters.includes(filter_name)
    })

    for (const [, filter] of included_filters) {
        if (is_filter_activated(filter, '_meta' in collection)) {
            active += 1
        }
    }
    return active
}

/**
 * Determine if the sort has been activated;
 * e.g. it differs  from the default sort.
 */
export function activated_sort(sort: CollectionSort) {
    const defaults = sort.defaults

    return {
        by: defaults.by !== sort.by,
        order: defaults.order !== sort.order,
    }
}

/**
 * Apply a stored selection and collapsed state onto a collection filter (used when restoring from lookup).
 * Handles both minimal stored shape { selection, collapsed? } and legacy full filter objects.
 */
function apply_stored_selection_to_filter(filter: Filter, stored: {selection?: unknown; collapsed?: boolean} | null | undefined) {
    if (stored === null || stored === undefined || !('selection' in stored)) return
    let value: unknown = stored.selection
    if (Array.isArray(filter.serialize)) {
        // JSON.stringify converts Infinity to null; restore it for SELECT_RANGE filters with infinity
        if (filter.type === 'SELECT_RANGE' && Array.isArray(value) && value.length >= 2 && filter.infinity && value[1] === null) {
            value = [value[0], Infinity]
        }
        replace_list_selection(filter, Array.isArray(value) ? value : [])
    } else {
        // `lookup_filters_sort` stored this filter's own selection.
        set_selection(filter, unchecked<FilterSelection>(value))
    }
    if (value && filter.collapsed && !('collapsed' in stored)) {
        // Only expand when the filter is actually activated (differs from default).
        // Filters at default (e.g. [0, Infinity] for stock_age_range) should stay collapsed.
        if (is_filter_activated(filter)) {
            filter.collapsed = false
        }
    }
    if ('collapsed' in stored && typeof stored.collapsed === 'boolean') {
        // When filter has default value, ignore stored collapsed: false - keep it collapsed.
        // This fixes the bug where stock_age at default [0, Infinity] was open by default.
        filter.collapsed = is_filter_activated(filter) ? stored.collapsed : true
    }
}

/**
 * Assign a filter selection from a value.
 */
export function assign_filter_selection(filter: Filter, value: string) {
    let filter_value
    if (Array.isArray(filter.serialize)) {
        filter_value = deserialize_filter_param(filter.serialize, value)
        replace_list_selection(filter, filter_value)
    } else {
        filter_value = deserialize_filter_param(filter.serialize, value)
        set_selection(filter, filter_value)
    }
    if (filter_value && filter.collapsed) {
        filter.collapsed = false
    }
}

/**
 * Set a filter back to its unset state.
 */
export function clear_filter(filter: Filter) {
    const unset = unset_filter_selection(filter)
    if (Array.isArray(filter.serialize)) {
        // An array `serialize` unsets to an array.
        replace_list_selection(filter, Array.isArray(unset) ? unset : [])
    } else {
        set_selection(filter, unset)
        if ('input' in filter) {
            // The filters with an input serialize as text or numbers, which unset to ''.
            filter.input = typeof unset === 'string' ? unset : ''
        }
    }

    filter.collapsed = true
}

/**
 * Set a filter back to its default meta state.
 */
export function default_filter(filter: Filter) {
    if (Array.isArray(filter.serialize)) {
        logger.debug(`[filters] default_filter (array) ${String(filter.default)}`)
        if (filter.type === 'SELECT_RANGE') {
            replace_list_selection(filter, normalizeSelectRangeDefault(filter))
        } else {
            // A filter with an array `serialize` has an array default.
            replace_list_selection(filter, Array.isArray(filter.default) ? filter.default : [])
        }
    } else {
        logger.debug(`[filters] default_filter for ${filter.title} (primitive) -> "${String(filter.default)}"`)
        set_selection(filter, filter.default)
        if ('input' in filter) {
            // The filters with an input are text-like; their meta default is the text.
            filter.input = String(filter.default ?? '')
        }
    }
    filter.collapsed = !is_filter_activated(filter)
}

/**
 * Deserialize filter url query parameters using one of the serializable
 * types: string, number, boolean, [string], [number]
 */
export function deserialize_filter_param(data_type: ['string'] | ['number'], value: string): string[] | number[]
export function deserialize_filter_param(data_type: string, value: string): string | number | boolean | undefined
export function deserialize_filter_param(
    data_type: Filter['serialize'],
    value: string,
): string | number | boolean | string[] | number[] | undefined
export function deserialize_filter_param(
    data_type: string | string[],
    value: string,
): string | number | boolean | string[] | number[] | undefined {
    if (data_type === 'string') {
        return value
    } else if (data_type === 'number') {
        return Number(value)
    } else if (data_type === 'boolean') {
        return value === 'true'
    } else if (Array.isArray(data_type)) {
        const array_type = data_type[0]

        let selection: string[] | number[] | undefined
        if (array_type === 'string' || array_type === '[string]') {
            selection = value.split(',')
        } else if (array_type === 'number' || array_type === '[number]') {
            selection = value.split(',').map((i) => {
                if (i === 'Infinity') {
                    return Infinity
                }
                return Number(i)
            })
        }

        return selection
    }
    return undefined
}

/** Resolve lookup path whether `State` exposes it as a thunk or as `Promise<string>`. */
async function resolve_lookup_path_field(path: (() => Promise<string>) | Promise<string>): Promise<string> {
    return typeof path === 'function' ? path() : path
}

/**
 * The path lookup is async, because there is a use-case in which
 * the lookup path has to be dynamic, using a *filter-specific* path.
 */
export async function filter_config_lookup_path(collection: CollectionProxy): Promise<string> {
    let lookup_path = ''
    const path = collection.state.lookup.path
    if (path) {
        lookup_path = await resolve_lookup_path_field(path)
    } else {
        lookup_path = collection.state.endpoint.path
    }

    return lookup_path
}

/**
 * Generate URL query parameters from active filters.
 */
export function filters_sort_to_url(filters: Filters, sort: CollectionSort, use_meta_default = false) {
    logger.debug('[filters] filters_sort_to_url')
    const params: Record<string, unknown> = {}
    const pathname = m.parsePathname(m.route.get())

    const params_from_url = parse_query_params_for_filters(pathname.params)
    delete params_from_url.meta

    let filters_activated = false
    for (const [filter_name, filter] of Object.entries(filters)) {
        if (is_filter_activated(filter, use_meta_default)) {
            filters_activated = true
            if (Array.isArray(filter.selection)) {
                if (filter.selection.length > 0) {
                    params[filter_name] = filter.selection.join(',')
                }
            } else {
                params[filter_name] = filter.selection
            }
        } else if (filter_name in params_from_url) {
            // Remove any inactive filters from the new URL.
            delete params_from_url[filter_name]
        }
    }

    const sort_diff = activated_sort(sort)
    const sort_activated = sort_diff.by || sort_diff.order

    if (sort_activated) {
        if (sort_diff.by) {
            params.sort_by = sort.by
        } else {
            delete params_from_url.sort_by
        }
        if (sort_diff.order) {
            params.sort_order = sort.order
        } else {
            delete params_from_url.sort_order
        }
    } else {
        delete params_from_url.sort_by
        delete params_from_url.sort_order
    }

    if (use_meta_default) {
        const meta_state_active = !filters_activated && !sort_activated
        if (meta_state_active) {
            params_from_url.meta = 'true'
        } else {
            // The meta state is not active, which means that the filters/sort
            // state is explicitly described in the URL; e.g. all filters that
            // are ACTIVE (not activated), must also be reflected in the URL.
            for (const [filter_name, filter] of Object.entries(filters)) {
                if (is_filter_activated(filter, false)) {
                    params[filter_name] = filter.selection
                } else {
                    delete params_from_url[filter_name]
                }
            }
        }
    }

    merge_deep(params_from_url, params)
    return to_query_params(params_from_url)
}

/**
 * Generate the initial filter datamodel from a filter description.
 * Data that can be derived is added to each filter, to keep the
 * initial data model as compact as possible.
 */
export function generate_filters<T extends FiltersDescription>(filters_description: T): FiltersFromDescription<T> {
    const filters: Filters = {}
    for (const [filter_key, filter_description] of Object.entries(filters_description)) {
        const filter_type = filter_description.type
        const derived_data = {
            count: 'count' in filter_description ? filter_description.count : false,
            loading: true,
            // A filter may carry its own key, for a collection whose wording differs under a name it
            // shares with every other list — see `FilterDefinition.title`.
            // @i18n ignore: filters.title.*
            title: filter_description.title ?? `filters.title.${filter_key}`,
        } as Partial<Filter>

        if (!('selection' in filter_description)) {
            if (filter_type === 'SELECT_SINGLE') {
                derived_data.selection = ''
            } else if (filter_type === 'SELECT_MULTIPLE') {
                derived_data.selection = []
            } else if (filter_type === 'SELECT_RANGE') {
                derived_data.selection = [
                    ...normalizeRangeTuple(filter_description.default, {
                        infinity: filter_description.infinity,
                        scaleMax: scale_max(filter_description.scale),
                    }),
                ]
            } else if (filter_type === 'TEXT') {
                Object.assign(derived_data, {
                    icon: 'search',
                    input: '',
                    selection: unset_serialized_selection(filter_description.serialize),
                })
            }
        }

        if (
            filter_type &&
            ['SELECT_MULTIPLE', 'SELECT_SINGLE', 'TEXT', 'TOGGLE'].includes(filter_type) &&
            !('options' in filter_description)
        ) {
            Object.assign(derived_data, {options: []})
        }

        // A description authors the fields its filter type has no derived default for.
        const built_filter = unchecked<Filter>({...filter_description, ...derived_data})
        built_filter.collapsed = !is_filter_activated(built_filter)
        filters[filter_key] = built_filter

        const built = built_filter
        if (built.type === 'SELECT_RANGE') {
            const sel = built.selection as number[]
            if (!Array.isArray(sel) || sel.length < 2) {
                const selection = built.selection as number[]
                selection.splice(0, selection.length, ...normalizeSelectRangeDefault(built))
            }
        }
    }

    // Each filter was built from the description under its key, which Object.entries can't follow.
    return unchecked<FiltersFromDescription<T>>(state(filters))
}

/**
 * Determine if a filter is activated.
 */
export function is_filter_activated(filter: Filter, use_meta_default = false): boolean {
    if (filter.disabled) {
        return false
    }

    // When using meta defaults, compare against the default value from meta
    // if it exists, otherwise fall back to unset comparison
    const comparison_value = use_meta_default && filter.default !== undefined ? filter.default : unset_filter_selection(filter)

    if (Array.isArray(filter.serialize)) {
        // A range filter acts a bit different; it always compares against
        // its default values when available, regardless of meta_default setting
        if (filter.type === 'SELECT_RANGE') {
            if (filter.default !== undefined) {
                return (
                    filter.selection.length === 2 &&
                    (filter.selection[0] !== filter.default[0] || filter.selection[1] !== filter.default[1])
                )
            } else {
                // Fallback to checking if selection has content
                return !!filter.selection.length
            }
        }

        // For array-based filters (SELECT_MULTIPLE), check if selection differs from comparison value.
        // An array `serialize` means an array selection, a pairing the Filter union can't express.
        if (use_meta_default && Array.isArray(filter.default)) {
            const defaults: unknown[] = filter.default
            const selection: unknown[] = Array.isArray(filter.selection) ? filter.selection : []
            // Compare arrays: active if different lengths or different contents
            if (selection.length !== defaults.length) {
                return true
            }
            // Check if arrays have the same content (bidirectional comparison)
            const selection_has_extra = selection.some((item) => !defaults.includes(item))
            const default_has_extra = defaults.some((item: unknown) => !selection.includes(item))
            return selection_has_extra || default_has_extra
        } else {
            // Regular filters just look for selection items when not using meta defaults
            return Array.isArray(filter.selection) && filter.selection.length > 0
        }
    } else {
        // For single-value filters, compare selection against the comparison value
        return filter.selection !== comparison_value
    }
}

/**
 * Store only filter selections and sort in the lookup table (no options, context, etc.)
 * and update the URL based on the current filter state.
 */
export async function lookup_filters_sort(collection: CollectionProxy) {
    const lookup_path = await filter_config_lookup_path(collection)
    // The modified timestamp is used to determine if the filter can be removed
    // after x amount of time. The store takes care of a periodic cleanup.
    const filters_to_store: Record<string, {selection: unknown; collapsed: boolean}> = {}

    for (const filter_name of Object.keys(collection.filters)) {
        const filter = collection.filters[filter_name]
        if (filter && 'selection' in filter) {
            // When filter has default value, always store collapsed: true so it stays closed on restore.
            // This fixes stock_age at [0, Infinity] being open by default.
            const collapsed = is_filter_activated(filter) ? filter.collapsed : true
            filters_to_store[filter_name] = {
                selection: copy_object(filter.selection),
                collapsed,
            }
        }
    }
    const lookup_data = {
        filters: filters_to_store,
        modified: Date.now(),
        sort: {
            by: collection.state.sort.by,
            order: collection.state.sort.order,
        },
    }
    if ($s.lookup[lookup_path]) {
        merge_deep($s.lookup[lookup_path], lookup_data)
    } else {
        $s.lookup[lookup_path] = lookup_data
    }
    logger.debug(`[collection-items] store filter/sort combination in lookup table: ${lookup_path}`)
    await store.save()

    // Reflect the active filter state in the url; include
    // sort_by and sort_order when they are part of the params.
    const sort: CollectionSort = collection.state.sort // sort is a CollectionSort but signals cause typing issues here
    if (collection.state.url_sync) {
        const filter_params = filters_sort_to_url(collection.filters, sort, '_meta' in collection)
        // Store current filter state in local storage.
        const {path} = m.parsePathname(m.route.get())
        await m.route.set(path, filter_params, {state: path})
        await nextTick()
    }
    collection.state.url_sync = true
}

/**
 * Render a filter based on its description.
 */
export function place_filter(
    collection: CollectionProxy,
    filter: Filter,
    filter_key: string,
    context?: ContextProvider<ContextProviderDataGeneric>,
) {
    const i18n = 'i18n' in filter ? filter.i18n : undefined
    switch (filter.type) {
        case 'TEXT':
            return <FilterText className={`filter-${filter_key}`} filter={filter} />
        case 'SELECT_SINGLE':
            if (filter.widget === 'workflow') {
                return (
                    <FilterWorkflow
                        className={`filter-${filter_key}`}
                        filter={filter}
                        collection={collection}
                        context={context}
                    />
                )
            }

            return (
                <FilterSelectSingle
                    className={`filter-${filter_key}`}
                    filter={filter}
                    filter_count={filter.count}
                    translate={i18n}
                />
            )
        case 'SELECT_RANGE':
            return <FilterSelectRange className={`filter-${filter_key}`} filter={filter} />
        case 'SELECT_MULTIPLE':
            return (
                <FilterSelectMultiple
                    className={`filter-${filter_key}`}
                    filter={filter}
                    filter_count={filter.count}
                    total={collection.state.total}
                    translate={i18n}
                />
            )
        case 'TOGGLE':
            return (
                <FilterToggle className={`filter-${filter_key}`} filter={filter} filter_count={filter.count} translate={i18n} />
            )
    }
}

/**
 * Render a filters object as a list of filter components.
 */
export function place_filters(collection: CollectionProxy, context?: ContextProvider<ContextProviderDataGeneric>) {
    const filters = collection.filters
    return Object.entries(filters)
        .flatMap(([filter_key, filter]) => {
            // include_snoozed is rendered inside FilterWorkflow, not as a standalone filter
            if (filter_key === 'include_snoozed') return []
            // Only filters with a placement option are rendered. Filters
            // with a min(imum) amount are only rendered, when there are at
            // least x options to choose from.
            if (!('placement' in filter)) return []
            const {placement} = filter
            if (placement.min && !('options' in filter && filter.options.length >= placement.min)) return []
            return [{filter, filter_key, order: placement.order}]
        })
        .toSorted((a, b) => a.order - b.order)
        .map(({filter, filter_key}) => {
            return place_filter(collection, filter, filter_key, context)
        })
}

/**
 * Return filters back to their initial state.
 * For meta-filters, the initial state is the default
 * value from the meta endpoint. For non-meta-filters,
 * the initial state is the unset state.
 */
export async function reset_filters_sort(collection: CollectionProxy) {
    const lookup_path = await filter_config_lookup_path(collection)
    if (lookup_path in $s.lookup) {
        delete $s.lookup[lookup_path]
        await store.save()
    }
    const filters = collection.filters
    for (const [filter_name, filter] of Object.entries(filters)) {
        if (collection.state.lookup.ignore_filters.includes(filter_name)) {
            continue
        }

        if (collection._meta) {
            default_filter(filter)
        } else {
            clear_filter(filter)
        }
    }

    merge_deep(collection.state.sort, {
        by: collection.state.sort.defaults.by,
        order: collection.state.sort.defaults.order,
    })
}

/**
 * Determine whether we load the filters from the store or from the URL.
 * @param collection
 */
export async function restore_filters_sort(collection: CollectionProxy): Promise<boolean> {
    await nextTick()
    const pathname = m.parsePathname(m.route.get())
    const url_params = parse_query_params_for_filters(pathname.params)
    // Whether to use the lookup filter/sort state or the URL filter/sort state.
    let use_filters_sort_lookup = false

    // First determine if there are any active filters in the URL.
    // If so, we don't want to restore filters from the store; only
    // when the default filter state is active; e.g. meta=true or no filters.
    if (collection.state.lookup.enabled) {
        if (collection._meta) {
            // With meta defaults, we check against the default filter state,
            // to determine if any filter is active in the URL.
            use_filters_sort_lookup = !Object.entries(url_params).some(([param_name, param_value]) => {
                const filter = collection.filters[param_name]
                if (filter) {
                    // Create a test object with URL value to check if it would be active; the value is
                    // deserialized by the filter's own `serialize`, so it is a selection of its type.
                    const test_filter = {...filter}
                    set_selection(test_filter, deserialize_filter_param(filter.serialize, decodeURI(param_value)))
                    return is_filter_activated(test_filter, true)
                }
                return false
            })
        } else {
            const filter_names = Object.keys(collection.filters)

            // Without meta; make sure that no filter is active, before restoring filters.
            use_filters_sort_lookup = !filter_names
                .filter((i) => !collection.state.lookup.ignore_filters.includes(i))
                .some((filter_name) => {
                    return filter_name in url_params
                })
        }
        logger.info(`[collection-items] lookup_state: ${use_filters_sort_lookup}${collection._meta ? ' (meta)' : ' (no meta)'}`)
    }

    const lookup_path = await filter_config_lookup_path(collection)

    if (!use_filters_sort_lookup) {
        logger.info(`[collection-items] skipping restored filters for: ${lookup_path}`)
        return false
    }

    if (!(lookup_path in $s.lookup)) {
        logger.info(`[collection-items] no stored filters found yet for: ${lookup_path}`)
        return false
    }

    logger.info(`[collection-items] restore filters from store: ${lookup_path}`)
    let stored_filters: CollectionLookup['filters']
    let stored_sort: Partial<CollectionSort>
    let lookup_entry: CollectionLookup
    try {
        const entry = $s.lookup[lookup_path]
        // Another owner's value under this path; reading its filters would fail the same way.
        if (typeof entry !== 'object') {
            throw new TypeError(`not a collection lookup: ${String(entry)}`)
        }
        lookup_entry = entry
        stored_filters = copy_object<CollectionLookup['filters'] | null>(lookup_entry.filters) ?? {}
        stored_sort = copy_object<CollectionLookup['sort'] | null>(lookup_entry.sort) ?? {}
    } catch (error) {
        logger.warn(`[collection-items] clearing incorrectly stored filters from store: ${lookup_path} ${String(error)}`)
        delete $s.lookup[lookup_path]
        await store.save()
        return false
    }

    // Validate stored filters against current filter definitions.
    // Filters that no longer exist or have changed structure (legacy full-object format) should be removed.
    let filters_changed = false
    for (const [filter_name, stored_filter] of Object.entries(stored_filters)) {
        const current_filter = collection.filters[filter_name]

        if (!current_filter) {
            logger.warn(`[collection-items] stored filter "${filter_name}" no longer exists, removing from store`)
            delete stored_filters[filter_name]
            filters_changed = true
            continue
        }

        // Legacy format: full filter object with type/serialize; validate structure
        if ('type' in stored_filter && stored_filter.type !== null) {
            if (
                stored_filter.type !== current_filter.type ||
                JSON.stringify(stored_filter.serialize) !== JSON.stringify(current_filter.serialize)
            ) {
                logger.warn(
                    `[collection-items] stored filter "${filter_name}" structure changed, disabling and removing from store`,
                )
                current_filter.disabled = true
                clear_filter(current_filter)
                delete stored_filters[filter_name]
                filters_changed = true
            }
        }
    }

    if (filters_changed) {
        lookup_entry.filters = stored_filters
        await store.save()
    }

    // Ignored filters: clear from store when not in URL; URL override is applied after we apply stored selections
    for (const filter_name of collection.state.lookup.ignore_filters) {
        if (!(filter_name in url_params)) {
            logger.info(`[collection-items] removing ignored filter: ${filter_name}`)
            delete stored_filters[filter_name]
            clear_filter(present(collection.filters[filter_name], `ignored filter ${filter_name}`))
        }
    }

    // Apply only stored selections to collection filters (no options, context, etc.)
    for (const filter_name of Object.keys(stored_filters)) {
        const current_filter = collection.filters[filter_name]
        if (current_filter) {
            apply_stored_selection_to_filter(current_filter, stored_filters[filter_name])
        }
    }

    // Ignored filters with URL: override with URL value
    for (const filter_name of collection.state.lookup.ignore_filters) {
        const url_value = url_params[filter_name]
        if (url_value !== undefined) {
            logger.info(`[collection-items] applying ignored filter value from url: ${filter_name}`)
            assign_filter_selection(present(collection.filters[filter_name], `ignored filter ${filter_name}`), url_value)
        }
    }

    merge_deep(collection.state.sort, stored_sort)

    if (collection.state.url_sync) {
        const path = pathname.path
        const sort: CollectionSort = collection.state.sort // sort is a CollectionSort but signals cause typing issues here
        const filter_params = filters_sort_to_url(collection.filters, sort, '_meta' in collection)
        await m.route.set(path, filter_params, {replace: true})
    }
    collection.state.url_sync = true
    return true
}

/** The selection of a filter with nothing selected, of the type its `serialize` names. */
export function unset_filter_selection(filter: Filter): FilterSelection {
    // SELECT_RANGE also uses array serialize (e.g. ['number']); unset must be the meta default
    // range, not [], or clear_filter + url_to_filters_sort leave no length-2 selection and the
    // range Slider never mounts.
    if (Array.isArray(filter.serialize) && filter.type === 'SELECT_RANGE') {
        return [...normalizeSelectRangeDefault(filter)]
    }
    return unset_serialized_selection(filter.serialize)
}

/**
 * Together with its counterpart ("filters_sort_to_url"), this function
 * generates the filters state from the URL.
 * @param collection
 * @param search_url If set, use this search_url ie m.route.get()
 * @returns
 */
export async function url_to_filters_sort(collection: CollectionProxy, search_url?: string) {
    // A filter like search has a different UI-flow than the ones in the filter-panel,
    // which makes it unsuitable to start with a collapsed state.
    const filters = collection.filters
    const sort = collection.state.sort
    const {params} = m.parsePathname(m.route.get())
    const route_params = search_url ? search_url : params
    const url_params = parse_query_params_for_filters(route_params)

    if ('meta' in url_params) {
        // The URL params are ignored; instead the defaults from the meta endpoint
        // are applied to the filters and sort. The url is pushed to the navigation
        // bar as soon as the filter combination changes.
        await reset_filters_sort(collection)
        return
    }

    logger.debug('[filters] url_to_filters_sort')

    // Assign URL params to related filters state.
    const active_filter_keys = Object.keys(filters)
    for (const [url_key, url_value] of Object.entries(url_params)) {
        const filter = filters[url_key]
        if (active_filter_keys.includes(url_key) && filter) {
            const value = decodeURI(url_value)
            if (Array.isArray(filter.serialize)) {
                replace_list_selection(filter, deserialize_filter_param(filter.serialize, value))
            } else {
                set_selection(filter, deserialize_filter_param(filter.serialize, value))
            }

            filter.collapsed = false
        }
    }
    // Assign default values to filters that are not in the URL.
    for (const [filter_name, filter] of Object.entries(filters)) {
        if (!(filter_name in url_params)) {
            clear_filter(filter)
        }
    }

    // Assign sort params from the url to the collection sort state.
    if (url_params.sort_by) {
        sort.by = url_params.sort_by
    }
    if (url_params.sort_order === 'asc' || url_params.sort_order === 'desc') {
        sort.order = url_params.sort_order
    }
}
